import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Search, RefreshCw, Syringe, TestTube, Microscope, ClipboardCheck, BadgeCheck,
    AlertTriangle, Clock, Loader2, ChevronRight, Lock, Ban, Building2, Timer,
    CalendarDays, X, Siren, Stethoscope,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Pagination from '../../components/Pagination';
import CommonLoader from '../../components/CommonLoader';
import usePermissions from '../../hooks/usePermissions';
import useSession from '../../hooks/useSession';
import { hospitalPatientCode } from '../../utils/patientCode';
import { formatTime } from '../../utils/clock';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import {
    getWorklist, getSections, collectSample, receiveSample, rejectSample,
} from '../../redux/labSlice';

const MySwal = withReactContent(Swal);

// THE LABORATORY WORKLIST — what is on the bench right now, and what has to
// happen to it next.
//
// A worklist is a QUERY, not a table. There is no lab_worklists row anywhere in
// the backend and there must never be one: the moment "what is waiting" is
// stored rather than derived, it can disagree with the samples and the results
// it claims to summarise, and a lab reading a stale queue leaves a tube in a
// rack all afternoon. Every bucket below is a filter over service_orders,
// lab_samples and lab_results as they stand this second.
//
// THE FIVE BUCKETS ARE THE FIVE PLACES A TUBE CAN PHYSICALLY BE. Not statuses
// invented for a dropdown — a stage of custody, each one ending with a different
// person doing a different thing:
//   to collect  -> nobody has drawn it yet; a phlebotomist goes to the patient
//   collected   -> it is drawn and in transit; the lab has not accepted it
//   on the bench-> the lab has accepted it and owes a number
//   to verify   -> a number exists and a SECOND person has to approve it
//   verified    -> approved, and waiting for somebody to release the report
// Tabs rather than a status filter because the counts belong on screen at all
// times: "eleven waiting to be verified" is the sentence that decides how the
// next hour is spent, and it must not be hidden inside a closed <select>.
//
// THE ACCESSION NUMBER IS THE LOUDEST THING ON EVERY ROW, deliberately, and
// deliberately not in the brand palette. It is the only identifier written on
// the tube itself, so it is what the eye is matching against a rack of labels —
// a wayfinding label, not chrome. Near-black monospace on white wins that
// contest against a row that also carries a name, an age, a test list and a
// clock; teal-on-white does not, and it would make the teal mean "important"
// instead of "this is the app". Same reasoning as the bin badge on the pharmacy
// counter.
//
// PRIORITY IS CODED THREE WAYS AT ONCE — a coloured rail down the left of the
// row, a filled badge, and the word STAT itself. Once, in colour alone, is not
// enough: roughly one man in twelve cannot separate the red from the amber, lab
// monitors are elderly, and the cost of missing a STAT troponin is not a
// cosmetic one.
//
// THIS SCREEN NEVER RELEASES A REPORT. Verification approves a value; release
// publishes a set, under a signature, and lives behind its own permission on the
// report screen. Putting a release button next to a verify button would make
// two decisions look like one, which is exactly the confusion the two-person
// rule exists to prevent.
//
// PERMISSIONS ARE NOT ENFORCED HERE. requireTenantFeature('lab') and
// requirePermission guard every route, and the model layer scopes every read to
// one hospital. The can() checks below only decide whether a control is drawn
// and, when it is not, SAY SO IN WORDS — a technician who cannot verify needs to
// know that from the screen, not from a 403 after they have typed for a minute.
//
// OUTSTANDING WORK IS NEVER HIDDEN BEHIND A DATE. Four of the five buckets are
// undone work — a tube ordered yesterday and still not drawn, or drawn and never
// run, is the single most important row a laboratory can be shown, and a date
// filter defaulting to today is exactly how it stops being shown. Those buckets
// are therefore unbounded and the day picker is switched off on them, with the
// reason on screen. Only 'verified' takes a date: it is a completed-work
// register that grows without limit and that nobody is waiting on. The same
// position the collection register takes, for the same reason.
//
// ---------------------------------------------------------------------------
// THE API THIS SCREEN CONSUMES, AND WHERE IT LIVES.
//
// Not here. Every call below goes through redux/labSlice.js — getWorklist,
// getSections, collectSample, receiveSample, rejectSample — and this file holds
// no URL of its own. That is the repair of a real defect: this screen and
// LabCollection.jsx each kept their own copy of the collection path and the two
// copies DISAGREED, one addressing the sample and one the order. Two spellings of
// one endpoint is not a style problem, it is a screen that 404s on the busiest
// action in the room. One caller, in the slice, is what makes that impossible.
//
// THERE IS NO SAMPLE ROW UNTIL SOMEBODY DRAWS THE TUBE, and that is why
// collection is addressed by ORDER while receive and reject are addressed by
// SAMPLE. An order that is never collected must not burn an accession number out
// of the year's sequence and leave a hole an auditor asks about
// (models/labSampleModel.js), so the row and its number are minted together at
// the moment of the draw. A 'to collect' entry therefore carries no sampleId and
// no accession number — the absence is the state, not a fault.
//
// A worklist row, with every field this file reads:
//   orderId, accessionNo, sampleId, sampleStatus, sampleType, containerType,
//   containerColour, collectionSite, priority ('routine'|'urgent'|'stat'),
//   patientId, patientName, patientAge, patientGender, doctorName,
//   testCount, pendingCount, enteredCount, verifiedCount, criticalCount,
//   tests: [{ testId, name, isOutsourced }], sectionNames: [],
//   orderedAt, collectedAt, receivedAt, dueAt, isOutsourced, partnerName
// Everything except orderId and sampleId is read defensively — a row missing its
// accession number renders as "not yet drawn" rather than as a crash.
// ---------------------------------------------------------------------------

const REFRESH_MS = 20000;
const SEARCH_DEBOUNCE_MS = 350;
const PAGE_SIZE = 25;

// The one bucket that is a register of finished work rather than a queue of
// unfinished work, and therefore the only one a day filter belongs on.
const DATED_BUCKET = 'verified';

const BUCKETS = [
    { id: 'pending_collection', icon: Syringe, fallback: 'To collect' },
    { id: 'collected', icon: TestTube, fallback: 'Collected' },
    { id: 'received', icon: Microscope, fallback: 'On the bench' },
    { id: 'resulted', icon: ClipboardCheck, fallback: 'To verify' },
    { id: 'verified', icon: BadgeCheck, fallback: 'Verified' },
];

const BUCKET_HINTS = {
    pending_collection: 'Ordered, not yet drawn. Somebody has to go to the patient.',
    collected: 'Drawn and on its way. The lab has not accepted it yet.',
    received: 'Accepted by the lab and owed a number.',
    resulted: 'A value has been entered. A second, registered person must approve it.',
    verified: 'Approved. Ready to be released as a signed report.',
};

// stat and urgent are the only two that change what anybody does. 'routine' gets
// no badge at all — a label on every row is a label on no row.
// `rail` paints the card's left edge, `railBorder` the table row's — the same
// stripe drawn by the two mechanisms each layout can actually honour. A
// background-coloured <span> inside a <td> does not stretch to the row height in
// any browser worth supporting, and a border does not exist on a flex child.
const PRIORITY_STYLES = {
    stat: {
        badge: 'bg-red-600 text-white',
        rail: 'bg-red-600',
        railBorder: 'border-red-600',
        row: 'bg-red-50/40',
        label: 'STAT',
    },
    urgent: {
        badge: 'bg-amber-500 text-white',
        rail: 'bg-amber-500',
        railBorder: 'border-amber-500',
        row: '',
        label: 'URGENT',
    },
};

const SAMPLE_TYPES = ['blood', 'serum', 'plasma', 'urine', 'stool', 'swab', 'tissue', 'fluid', 'other'];
const COLLECTION_SITES = ['lab', 'home', 'ward', 'otc'];

const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

/** Local calendar day as 'YYYY-MM-DD'. Never via toISOString(), which is UTC and
 *  hands back yesterday for anybody east of Greenwich after teatime — on a
 *  worklist that reads as an empty lab. */
const todayLocal = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// The one clock format ("2:30 PM", utils/clock); '' when not a date.
const fmtClock = (iso) => formatTime(iso);

/** Minutes from now until `iso`. Negative once the turnaround time is blown. */
const minutesUntil = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return Math.round((d.getTime() - Date.now()) / 60000);
};

const fmtDelay = (mins) => {
    const abs = Math.abs(mins);
    if (abs < 60) return `${abs}m`;
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return m ? `${h}h ${m}m` : `${h}h`;
};

/* ------------------------------------------------------------------ *
 * Presentational pieces
 *
 * Outside the component on purpose: none of them reads page state, and keeping
 * them inside would rebuild every one of them on each keystroke in the search
 * box, which is the one place on this screen where that cost is felt.
 * ------------------------------------------------------------------ */

/**
 * The accession number, drawn once and drawn the same everywhere.
 *
 * When there is none the answer is not '-'. An order with no accession number
 * has no tube in the building, and saying "not drawn" is the finding — it is the
 * difference between "I cannot find the sample" and "there is no sample".
 */
function AccessionBadge({ accessionNo, testId }) {
    const { t } = useTranslation();
    if (!accessionNo) {
        return (
            <span
                data-testid={testId}
                title={t('labworklist.no_accession_hint', 'No sample has been drawn for this order yet')}
                className="inline-flex items-center gap-1 px-2.5 py-2 rounded-xl bg-amber-100 border-2 border-dashed border-amber-400 text-amber-700 text-[11px] font-bold uppercase tracking-wide"
            >
                <Syringe className="w-3.5 h-3.5" />
                {t('labworklist.not_drawn', 'Not drawn')}
            </span>
        );
    }
    return (
        <span
            data-testid={testId}
            className="inline-flex items-center px-3 py-2 rounded-xl bg-slate-900 text-white shadow-md font-mono text-lg leading-none font-black tracking-wider"
        >
            {accessionNo}
        </span>
    );
}

/** STAT and URGENT. Word + colour + a filled shape, never colour alone. */
function PriorityBadge({ priority }) {
    const style = PRIORITY_STYLES[String(priority || '').toLowerCase()];
    if (!style) return null;
    return (
        <span
            data-testid={`priority-${priority}`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-black uppercase tracking-widest ${style.badge}`}
        >
            {priority === 'stat' ? <Siren className="w-3 h-3" /> : <Timer className="w-3 h-3" />}
            {style.label}
        </span>
    );
}

/**
 * How far through the order is: N of M entered, and how many of those approved.
 *
 * A count, not a percentage. "4 of 6" tells a technician there are two tubes
 * still to run; "67%" tells them nothing they can act on.
 */
function ResultProgress({ row }) {
    const { t } = useTranslation();
    const total = toNum(row.testCount);
    const entered = toNum(row.enteredCount);
    const verified = toNum(row.verifiedCount);
    if (!total) return <span className="text-[11px] text-slate-400">—</span>;

    const pctEntered = Math.min(100, Math.round(((entered + verified) / total) * 100));
    const pctVerified = Math.min(100, Math.round((verified / total) * 100));

    return (
        <div className="min-w-[120px]">
            <div className="relative h-1.5 rounded-full bg-slate-200 overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-[#9DB3B1]" style={{ width: `${pctEntered}%` }} />
                <div className="absolute inset-y-0 left-0 bg-[#2D8A8A]" style={{ width: `${pctVerified}%` }} />
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
                {t('labworklist.progress', '{{entered}} of {{total}} entered', {
                    entered: entered + verified,
                    total,
                })}
                {verified > 0 && (
                    <span className="text-[#2D8A8A] font-semibold">
                        {` · ${t('labworklist.progress_verified', '{{n}} verified', { n: verified })}`}
                    </span>
                )}
            </div>
        </div>
    );
}

/**
 * A control the signed-in user may not use.
 *
 * Drawn rather than hidden, with the reason on it. Hiding it would leave a
 * technician staring at a row nothing can be done to and no way to find out why;
 * the useful answer is "you can enter results but not verify them — ask whoever
 * holds lab.result.verify", and the only place that sentence fits is here.
 */
function LockedAction({ label, reason, testId }) {
    return (
        <span
            data-testid={testId}
            title={reason}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-400 text-xs font-semibold cursor-not-allowed"
        >
            <Lock className="w-3.5 h-3.5" />
            {label}
        </span>
    );
}

/**
 * Collecting a sample.
 *
 * The tube is PREFILLED FROM THE TEST MASTER and not chosen here. The master
 * already declares the specimen and the container for every test on the order
 * (labTestModel.sampleType / containerType / containerColour), so the
 * phlebotomist is confirming what is in their hand, not deciding what it should
 * have been. Leaving it blank for them to pick invites a serum test drawn into
 * an EDTA tube, which is discovered at the analyser and costs the patient a
 * second needle.
 *
 * The accession number is NOT entered here and is not shown before saving: it is
 * generated server-side at the moment of collection from a per-tenant, per-year
 * counter, because two phlebotomists pressing this button in the same second
 * must not be able to produce the same label.
 *
 * THERE IS NO REMARKS BOX, and its absence is the fix rather than an omission.
 * One stood here and the collection endpoint does not read the field — a
 * phlebotomist could type "difficult draw, second attempt" and watch it be
 * discarded by a form that looked like it had recorded it. The note about a
 * specimen belongs to receiving it, which is where the server actually appends
 * one; a box that silently throws a clinical observation away is worse than no
 * box, because it also stops anybody writing it down somewhere that works.
 */
function CollectModal({ open, row, busy, onClose, onConfirm }) {
    const { t } = useTranslation();
    const [sampleType, setSampleType] = useState('blood');
    const [containerType, setContainerType] = useState('');
    const [collectionSite, setCollectionSite] = useState('lab');

    useEffect(() => {
        if (!open || !row) return;
        setSampleType(SAMPLE_TYPES.includes(row.sampleType) ? row.sampleType : 'blood');
        setContainerType(row.containerType || '');
        setCollectionSite(COLLECTION_SITES.includes(row.collectionSite) ? row.collectionSite : 'lab');
    }, [open, row]);

    if (!open || !row) return null;

    return (
        <div
            data-testid="collect-modal"
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
            role="dialog"
            aria-modal="true"
        >
            <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="flex items-start justify-between gap-3 px-5 py-4 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                    <div>
                        <h2 className="text-lg font-bold">{t('labworklist.collect_title', 'Collect sample')}</h2>
                        <p className="text-xs text-white/80 mt-0.5">
                            {[row.patientName || t('common.patient_label', 'Patient'), row.accessionNo].filter(Boolean).join(' · ')}
                        </p>
                    </div>
                    <button
                        type="button"
                        data-testid="collect-modal-close"
                        onClick={onClose}
                        className="p-1 rounded-lg hover:bg-white/20 cursor-pointer"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    <p className="text-xs text-slate-500">
                        {t(
                            'labworklist.collect_hint',
                            'The tube below comes from the test master. Change it only if what you actually drew is different — the accession number is generated when you save.'
                        )}
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label className="block">
                            <span className="text-xs font-semibold text-slate-600">{t('labworklist.sample_type', 'Specimen')}</span>
                            <select
                                data-testid="collect-sample-type"
                                value={sampleType}
                                onChange={(e) => setSampleType(e.target.value)}
                                className="tt-select mt-1 w-full p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                            >
                                {SAMPLE_TYPES.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </label>

                        <label className="block">
                            <span className="text-xs font-semibold text-slate-600">{t('labworklist.container', 'Container')}</span>
                            <input
                                data-testid="collect-container"
                                type="text"
                                value={containerType}
                                maxLength={60}
                                onChange={(e) => setContainerType(e.target.value.replace(/[<>]/g, ''))}
                                placeholder={t('labworklist.container_placeholder', 'e.g. EDTA (lavender)')}
                                className="mt-1 w-full p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                            />
                            {row.containerColour && (
                                <span className="text-[11px] text-slate-400 mt-1 inline-block">
                                    {t('labworklist.cap_colour', 'Cap: {{colour}}', { colour: row.containerColour })}
                                </span>
                            )}
                        </label>

                        <label className="block">
                            <span className="text-xs font-semibold text-slate-600">{t('labworklist.collection_site', 'Drawn at')}</span>
                            <select
                                data-testid="collect-site"
                                value={collectionSite}
                                onChange={(e) => setCollectionSite(e.target.value)}
                                className="tt-select mt-1 w-full p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                            >
                                {COLLECTION_SITES.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </label>

                    </div>
                </div>

                <div className="flex justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-100">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-xl border border-gray-300 text-slate-600 text-sm font-semibold hover:bg-white cursor-pointer"
                    >
                        {t('labworklist.cancel', 'Cancel')}
                    </button>
                    <button
                        type="button"
                        data-testid="collect-confirm"
                        disabled={busy}
                        onClick={() => onConfirm({ sampleType, containerType: containerType.trim(), collectionSite })}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white text-sm font-semibold shadow-lg ${busy ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:shadow-xl'}`}
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Syringe className="w-4 h-4" />}
                        {t('labworklist.collect_confirm', 'Record collection')}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ *
 * The screen
 * ------------------------------------------------------------------ */

export default function LabWorklist() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { can } = usePermissions();
    const session = useSession();
    const [searchParams] = useSearchParams();

    // WHOSE LABORATORY. A doctor, an admin or an employee carries their hospital
    // in the verified token and cannot name another, so what is sent here is
    // ignored for them. Super Admin is unscoped everywhere in this codebase and
    // holds no tenant at all — every one of these endpoints answers 400 until the
    // organisation is named, which is what ?tenantId= on the URL is for. Passing
    // it unconditionally is what lets one screen serve both instead of growing a
    // second code path only Super Admin ever exercises.
    const tenantId = searchParams.get('tenantId') || session.tenantId || '';

    const canCollect = can('lab.sample.collect');
    const canEnter = can('lab.result.enter');
    const canVerify = can('lab.result.verify');

    const [bucket, setBucket] = useState('received');
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState({});
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshedAt, setRefreshedAt] = useState(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [date, setDate] = useState(() => todayLocal());
    const [sectionId, setSectionId] = useState('');
    const [sections, setSections] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(PAGE_SIZE);
    const [refreshKey, setRefreshKey] = useState(0);

    const [collectRow, setCollectRow] = useState(null);
    const [busyOrderId, setBusyOrderId] = useState(null);

    // Paused while a dialog is open. A poll that lands mid-collection would swap
    // the row out from under the modal and leave the phlebotomist confirming a
    // tube for whoever the list moved into that position. Held in a ref so the
    // interval below reads the current answer without being torn down and
    // rebuilt — a poll that restarts on every state change never fires.
    const pollPausedRef = useRef(false);
    useEffect(() => {
        pollPausedRef.current = !!collectRow || !!busyOrderId;
    }, [collectRow, busyOrderId]);

    /* ---- the bench filter ---- */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const body = await dispatch(getSections({ status: 'active', tenantId })).unwrap();
                if (!cancelled) setSections(body?.data || []);
            } catch {
                // A missing section list narrows nothing and blocks nobody. The
                // worklist without its filter is still the worklist, so this
                // stays silent rather than raising a toast on a screen somebody
                // opened to do something else.
            }
        })();
        return () => { cancelled = true; };
    }, [dispatch, tenantId]);

    /* ---- search ---- */
    useEffect(() => {
        const id = setTimeout(() => {
            setDebouncedSearch(searchQuery.trim());
            setCurrentPage(1);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(id);
    }, [searchQuery]);

    // A search is a hunt for one named person or one accession number, and the
    // tube being looked for is usually the one that did NOT come back today —
    // that is why somebody is searching. So the day filter is dropped while a
    // term is present, and the screen says so rather than quietly returning
    // nothing.
    const searching = debouncedSearch.length > 0;
    // See the header: only the completed-work register is date-bounded. A queue
    // of undone work bounded by a date is a queue that hides its own backlog.
    const dateApplies = bucket === DATED_BUCKET && !searching;

    const load = useCallback(async ({ quiet = false } = {}) => {
        if (!quiet) setLoading(true);
        try {
            const body = await dispatch(getWorklist({
                bucket,
                page: currentPage,
                limit: itemsPerPage,
                search: debouncedSearch,
                // Empty on every bucket but the register of finished work, and
                // dropped by the slice rather than sent as `?date=` — see above
                // on why a queue of undone work must not be bounded by a day.
                date: !debouncedSearch && bucket === DATED_BUCKET ? date : '',
                sectionId,
                tenantId,
            })).unwrap();
            setRows(body?.data || []);
            setCounts(body?.counts || {});
            setTotal(toNum(body?.total));
            setError(null);
            setRefreshedAt(new Date());
        } catch (err) {
            setError(extractErrorMessage(err, t('labworklist.load_error', 'Could not load the worklist')));
        } finally {
            if (!quiet) setLoading(false);
        }
    }, [dispatch, bucket, currentPage, itemsPerPage, debouncedSearch, date, sectionId, tenantId, t]);

    useEffect(() => { load(); }, [load, refreshKey]);

    // The lab is a shared room: somebody else collects the tube you are looking
    // at, and the row has to move without anybody pressing anything. Quiet, so
    // the table never blinks — a spinner every twenty seconds on a screen that
    // is open all day is how people stop reading it.
    useEffect(() => {
        const id = setInterval(() => {
            if (!pollPausedRef.current) load({ quiet: true });
        }, REFRESH_MS);
        return () => clearInterval(id);
    }, [load]);

    const refetch = () => setRefreshKey((k) => k + 1);

    /* -------------------------------------------------------------- *
     * Actions
     * -------------------------------------------------------------- */

    const handleCollect = async (payload) => {
        const row = collectRow;
        if (!row?.orderId) return;
        setBusyOrderId(row.orderId);
        try {
            // Addressed by ORDER, because until this call returns there is no
            // sample row to address — it and its accession number are created
            // together, inside one transaction, so an order that is never drawn
            // leaves no gap in the year's sequence.
            const body = await dispatch(collectSample({ orderId: row.orderId, ...payload, tenantId })).unwrap();
            const accessionNo = body?.data?.accessionNo || body?.accessionNo;
            setCollectRow(null);
            // The accession number is read out to the label printer and written
            // on the tube, so it is repeated back rather than left for the
            // technician to find by reopening the row.
            toast.success(
                accessionNo
                    ? t('labworklist.collected_with_no', 'Collected — accession {{no}}', { no: accessionNo })
                    : t('labworklist.collected', 'Sample recorded as collected')
            );
            refetch();
        } catch (err) {
            toast.warn(extractErrorMessage(err, t('labworklist.collect_error', 'Could not record the collection')));
        } finally {
            setBusyOrderId(null);
        }
    };

    const handleReceive = async (row) => {
        if (!row.sampleId) return;
        setBusyOrderId(row.orderId);
        try {
            await dispatch(receiveSample({ sampleId: row.sampleId, tenantId })).unwrap();
            toast.success(t('labworklist.received', 'Sample received at the bench'));
            refetch();
        } catch (err) {
            toast.warn(extractErrorMessage(err, t('labworklist.receive_error', 'Could not receive the sample')));
        } finally {
            setBusyOrderId(null);
        }
    };

    /**
     * Rejecting a sample.
     *
     * The reason is mandatory and is not a dropdown of four options. A rejection
     * means the patient is stuck with a second needle, and "haemolysed",
     * "clotted", "insufficient volume" and "unlabelled tube" lead to four
     * different conversations — with the ward, with the phlebotomist, or with
     * nobody. It is also the row a NABL auditor reads, so it has to say
     * something.
     */
    const handleReject = async (row) => {
        if (!row.sampleId) return;
        const result = await MySwal.fire({
            title: t('labworklist.reject_title', 'Reject this sample?'),
            text: t(
                'labworklist.reject_text',
                'The patient will have to be drawn again. Say why — this is the record the ward and the auditor read.'
            ),
            icon: 'warning',
            input: 'text',
            inputPlaceholder: t('labworklist.reject_placeholder', 'e.g. haemolysed, clotted, insufficient volume'),
            inputAttributes: { maxlength: 200 },
            inputValidator: (value) => (
                String(value || '').trim().length < 4
                    ? t('labworklist.reject_reason_required', 'A reason is required')
                    : undefined
            ),
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('labworklist.reject', 'Reject sample'),
            cancelButtonText: t('labworklist.cancel', 'Cancel'),
        });
        if (!result.isConfirmed) return;

        setBusyOrderId(row.orderId);
        try {
            await dispatch(rejectSample({
                sampleId: row.sampleId,
                rejectionReason: String(result.value).trim(),
                tenantId,
            })).unwrap();
            toast.success(t('labworklist.rejected', 'Sample rejected — a fresh draw is needed'));
            refetch();
        } catch (err) {
            toast.warn(extractErrorMessage(err, t('labworklist.reject_error', 'Could not reject the sample')));
        } finally {
            setBusyOrderId(null);
        }
    };

    const openBench = (row) => navigate(`/lab/results/${row.orderId}`);

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    /* -------------------------------------------------------------- *
     * Per-row actions, decided by which bucket the row is in
     *
     * One button per row, and it is the NEXT thing that has to happen to that
     * tube. A row of five buttons of which four are wrong is how a collected
     * sample gets marked received before it has physically arrived.
     * -------------------------------------------------------------- */
    const primaryAction = (row) => {
        const busy = busyOrderId === row.orderId;

        if (bucket === 'pending_collection') {
            if (!canCollect) {
                return (
                    <LockedAction
                        testId={`locked-collect-${row.orderId}`}
                        label={t('labworklist.collect', 'Collect')}
                        reason={t('labworklist.no_collect_permission', 'You do not have permission to collect samples (lab.sample.collect)')}
                    />
                );
            }
            // A row in this bucket has no sampleId and no accession number, by
            // design — the tube does not exist yet. The button is gated on the
            // ORDER, which is what collection is addressed by; an earlier version
            // disabled it whenever sampleId was missing, which was every row in
            // the one bucket the button belongs to.
            return (
                <button
                    type="button"
                    data-testid={`collect-${row.orderId}`}
                    disabled={busy}
                    onClick={() => setCollectRow(row)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white text-xs font-semibold shadow ${busy ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'}`}
                >
                    <Syringe className="w-3.5 h-3.5" />
                    {t('labworklist.collect', 'Collect')}
                </button>
            );
        }

        if (bucket === 'collected') {
            if (!canCollect) {
                return (
                    <LockedAction
                        testId={`locked-receive-${row.orderId}`}
                        label={t('labworklist.receive', 'Receive')}
                        reason={t('labworklist.no_collect_permission', 'You do not have permission to collect samples (lab.sample.collect)')}
                    />
                );
            }
            return (
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        data-testid={`receive-${row.orderId}`}
                        disabled={busy}
                        onClick={() => handleReceive(row)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white text-xs font-semibold shadow ${busy ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'}`}
                    >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Microscope className="w-3.5 h-3.5" />}
                        {t('labworklist.receive', 'Receive')}
                    </button>
                    <button
                        type="button"
                        data-testid={`reject-${row.orderId}`}
                        disabled={busy}
                        title={t('labworklist.reject_hint', 'The tube is unusable and the patient must be drawn again')}
                        onClick={() => handleReject(row)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border border-red-200 text-red-600 bg-red-50 text-xs font-semibold ${busy ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-red-100'}`}
                    >
                        <Ban className="w-3.5 h-3.5" />
                        {t('labworklist.reject_short', 'Reject')}
                    </button>
                </div>
            );
        }

        if (bucket === 'received') {
            if (!canEnter) {
                return (
                    <LockedAction
                        testId={`locked-enter-${row.orderId}`}
                        label={t('labworklist.enter_results', 'Enter results')}
                        reason={t('labworklist.no_enter_permission', 'You do not have permission to enter results (lab.result.enter)')}
                    />
                );
            }
            return (
                <button
                    type="button"
                    data-testid={`enter-${row.orderId}`}
                    onClick={() => openBench(row)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white text-xs font-semibold shadow cursor-pointer hover:shadow-md"
                >
                    <ClipboardCheck className="w-3.5 h-3.5" />
                    {t('labworklist.enter_results', 'Enter results')}
                </button>
            );
        }

        if (bucket === 'resulted') {
            if (!canVerify) {
                return (
                    <div className="flex flex-col items-start gap-1">
                        <LockedAction
                            testId={`locked-verify-${row.orderId}`}
                            label={t('labworklist.verify', 'Verify')}
                            reason={t('labworklist.no_verify_permission', 'Verification is a second person\'s signature and needs lab.result.verify')}
                        />
                        <button
                            type="button"
                            data-testid={`open-${row.orderId}`}
                            onClick={() => openBench(row)}
                            className="text-[11px] text-[#2D8A8A] font-semibold underline underline-offset-2 cursor-pointer"
                        >
                            {t('labworklist.open_readonly', 'Open to read')}
                        </button>
                    </div>
                );
            }
            return (
                <button
                    type="button"
                    data-testid={`verify-${row.orderId}`}
                    onClick={() => openBench(row)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-900 text-white text-xs font-bold shadow cursor-pointer hover:bg-slate-800"
                >
                    <BadgeCheck className="w-3.5 h-3.5 text-[#4ABDBD]" />
                    {t('labworklist.verify', 'Verify')}
                </button>
            );
        }

        // verified — nothing left for the bench to do. Releasing the report is a
        // separate signature on a separate screen behind lab.report.release, and
        // putting its button here would make two decisions look like one.
        return (
            <button
                type="button"
                data-testid={`open-${row.orderId}`}
                onClick={() => openBench(row)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-[#4ABDBD] text-[#2D8A8A] text-xs font-semibold cursor-pointer hover:bg-[#4ABDBD]/10"
            >
                {t('labworklist.open', 'Open')}
                <ChevronRight className="w-3.5 h-3.5" />
            </button>
        );
    };

    const indexOfFirst = (currentPage - 1) * itemsPerPage;

    // See the banner below on why this is summed from the rows rather than read
    // off `counts`.
    const criticalOnPage = rows.reduce((n, r) => n + toNum(r.criticalCount), 0);

    /* -------------------------------------------------------------- *
     * Render
     * -------------------------------------------------------------- */

    return (
        <div data-testid="lab-worklist-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <div className="ml-1">
                    <h1
                        data-testid="lab-worklist-title"
                        className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                    >
                        {t('labworklist.title', 'Lab worklist')}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">
                        {t('labworklist.subtitle', 'What is on the bench right now, and what has to happen to it next.')}
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    {refreshedAt && (
                        <span className="text-[11px] text-slate-400 hidden sm:inline">
                            {t('labworklist.updated_at', 'Updated {{time}}', { time: fmtClock(refreshedAt.toISOString()) })}
                        </span>
                    )}
                    <button
                        type="button"
                        data-testid="lab-worklist-refresh"
                        onClick={refetch}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[#4ABDBD] text-[#2D8A8A] font-semibold text-sm hover:bg-[#4ABDBD]/10 cursor-pointer whitespace-nowrap"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        {t('labworklist.refresh', 'Refresh')}
                    </button>
                </div>
            </div>

            {/* The five stages of custody, with their counts. */}
            <div className="mb-4 overflow-x-auto">
                <div className="relative inline-flex min-w-full sm:min-w-0 bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-1">
                    {BUCKETS.map((b) => {
                        const Icon = b.icon;
                        const active = bucket === b.id;
                        const n = toNum(counts[b.id]);
                        return (
                            <button
                                key={b.id}
                                type="button"
                                data-testid={`lab-bucket-${b.id}`}
                                title={t(`labworklist.hint_${b.id}`, BUCKET_HINTS[b.id])}
                                onClick={() => { setBucket(b.id); setCurrentPage(1); }}
                                className={`relative px-4 py-2 text-sm rounded-xl transition-colors duration-300 z-10 whitespace-nowrap ${active ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
                            >
                                {active && (
                                    <motion.div
                                        layoutId="activeLabBucket"
                                        className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                    />
                                )}
                                <span className="relative z-20 inline-flex items-center gap-2">
                                    <Icon className="w-4 h-4" />
                                    {t(`labworklist.bucket_${b.id}`, b.fallback)}
                                    <span
                                        className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-bold ${active ? 'bg-white/25 text-white' : 'bg-slate-200 text-slate-600'}`}
                                    >
                                        {n}
                                    </span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Criticals outrank every filter on this page.

                COUNTED FROM THE ROWS ON SCREEN, not from a lab-wide total. This
                banner used to read `counts.critical`, which the worklist endpoint
                does not return — `counts` holds the five bucket sizes and nothing
                else, so the banner silently never appeared, on the one screen
                where silence is the worst possible failure. What the API does
                return is `criticalCount` per row, so that is what is added up,
                and the wording says "on this page" rather than claiming a total
                the screen cannot see. Same position the test master takes on its
                configuration-gap count, for the same reason: a number nobody can
                reconcile with what is in front of them sends people hunting
                through pages for rows that are not there. */}
            {criticalOnPage > 0 && (
                <div
                    data-testid="critical-banner"
                    className="mb-4 flex items-start gap-3 p-3 rounded-xl bg-red-50 border-2 border-red-300"
                >
                    <Siren className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-800">
                        <span className="font-bold">
                            {t('labworklist.critical_count', '{{n}} critical result(s) on this page', { n: criticalOnPage })}
                        </span>
                        <p className="text-xs text-red-700 mt-0.5">
                            {t(
                                'labworklist.critical_hint',
                                'A critical value has to reach a clinician by telephone before anybody waits for the report. Open the order and record who was told.'
                            )}
                        </p>
                    </div>
                </div>
            )}

            <div className="mb-5 flex flex-col sm:flex-row gap-2 sm:items-center">
                <div className="relative w-full sm:w-80">
                    <input
                        data-testid="lab-worklist-search"
                        type="text"
                        placeholder={t('labworklist.search_placeholder', 'Accession no / patient / order…')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value.replace(/[<>]/g, '').slice(0, 50))}
                        maxLength={50}
                        className="w-full p-2 pl-10 pr-8 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                    />
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                    {searchQuery && (
                        <button
                            type="button"
                            onClick={() => setSearchQuery('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>

                {/* Enabled only on the completed-work register — see the header. */}
                <div className="relative w-full sm:w-48">
                    <input
                        data-testid="lab-worklist-date"
                        type="date"
                        value={date}
                        disabled={!dateApplies}
                        title={dateApplies
                            ? undefined
                            : t('labworklist.date_disabled_hint', 'Outstanding work is never filtered by date — a tube from yesterday that nobody has drawn is the row you most need to see.')}
                        onChange={(e) => { setDate(e.target.value); setCurrentPage(1); }}
                        className={`w-full p-2 pl-10 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${dateApplies ? '' : 'opacity-40 cursor-not-allowed'}`}
                    />
                    <CalendarDays className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                </div>

                <select
                    data-testid="lab-worklist-section"
                    value={sectionId}
                    onChange={(e) => { setSectionId(e.target.value); setCurrentPage(1); }}
                    className="tt-select w-full sm:w-52 p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                >
                    <option value="">{t('labworklist.all_sections', 'All benches')}</option>
                    {sections.map((s) => (
                        <option key={s.sectionId} value={s.sectionId}>{s.name}</option>
                    ))}
                </select>

                {searching ? (
                    <span
                        data-testid="searching-all-dates"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#4ABDBD]/10 text-[#2D8A8A] text-[11px] font-semibold whitespace-nowrap"
                    >
                        <Clock className="w-3.5 h-3.5" />
                        {t('labworklist.searching_all_dates', 'Searching every day, not just this one')}
                    </span>
                ) : !dateApplies && (
                    <span
                        data-testid="backlog-included"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#4ABDBD]/10 text-[#2D8A8A] text-[11px] font-semibold whitespace-nowrap"
                    >
                        <Clock className="w-3.5 h-3.5" />
                        {t('labworklist.backlog_included', 'Backlog included — nothing outstanding is hidden by a date')}
                    </span>
                )}
            </div>

            {error && (
                <div data-testid="lab-worklist-error" className="mb-4 flex items-center gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    {error}
                </div>
            )}

            {loading ? (
                <div className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('labworklist.loading', 'Loading...')}</span>
                </div>
            ) : (
                <>
                    {/* Table on a bench monitor; cards on the phlebotomist's phone. */}
                    <div className="hidden lg:block overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid="lab-worklist-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th className="p-3 pl-2 text-left text-xs font-bold uppercase tracking-wider w-12 border-l-4 border-transparent">#</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labworklist.accession', 'Accession')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labworklist.patient', 'Patient')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labworklist.tests', 'Tests')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labworklist.progress_col', 'Progress')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labworklist.timing', 'Timing')}</th>
                                    <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('labworklist.next_step', 'Next step')}</th>
                                </tr>
                            </thead>
                            <tbody data-testid="lab-worklist-body">
                                {rows.length > 0 ? rows.map((row, index) => {
                                    const priority = String(row.priority || 'routine').toLowerCase();
                                    const style = PRIORITY_STYLES[priority];
                                    const overdueMins = minutesUntil(row.dueAt);
                                    const critical = toNum(row.criticalCount);
                                    return (
                                        <tr
                                            key={row.orderId}
                                            data-testid={`lab-row-${row.orderId}`}
                                            className={`border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/60 hover:to-cyan-50/60 transition-all duration-300 ${style?.row || ''}`}
                                        >
                                            {/* The priority rail. Colour is the third coding, not the first. */}
                                            <td className={`p-3 pl-2 text-xs text-gray-500 border-l-4 ${style?.railBorder || 'border-transparent'}`}>
                                                {indexOfFirst + index + 1}
                                            </td>

                                            <td className="p-3">
                                                <div className="flex flex-col items-start gap-1.5">
                                                    <AccessionBadge accessionNo={row.accessionNo} testId={`accession-${row.orderId}`} />
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <PriorityBadge priority={priority} />
                                                        {row.isOutsourced && (
                                                            <span
                                                                title={t('labworklist.outsourced_hint', 'Referred out — the result arrives from the partner lab')}
                                                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-100 text-purple-700 text-[10px] font-bold uppercase tracking-wide"
                                                            >
                                                                <Building2 className="w-3 h-3" />
                                                                {row.partnerName || t('labworklist.outsourced', 'Referred')}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="p-3 text-xs text-gray-600">
                                                <div className="font-semibold text-gray-700">{row.patientName || t('common.patient_label', 'Patient')}</div>
                                                <div className="text-[11px] text-slate-400">
                                                    {[row.patientAge, row.patientGender, hospitalPatientCode(row)].filter(Boolean).join(' · ')}
                                                </div>
                                                {row.doctorName && (
                                                    <div className="text-[11px] text-slate-400 mt-1 inline-flex items-center gap-1">
                                                        <Stethoscope className="w-3 h-3" />
                                                        {row.doctorName}
                                                    </div>
                                                )}
                                            </td>

                                            <td className="p-3 text-xs text-gray-600">
                                                <div className="flex flex-wrap gap-1 max-w-[260px]">
                                                    {(row.tests || []).slice(0, 3).map((test) => (
                                                        <span key={test.testId} className="px-2 py-0.5 rounded bg-gray-100 text-[11px] text-gray-600">
                                                            {test.name}
                                                        </span>
                                                    ))}
                                                    {(row.tests || []).length > 3 && (
                                                        <span className="px-2 py-0.5 rounded bg-slate-200 text-[11px] text-slate-600 font-semibold">
                                                            {`+${row.tests.length - 3}`}
                                                        </span>
                                                    )}
                                                    {!(row.tests || []).length && toNum(row.testCount) > 0 && (
                                                        <span className="text-[11px] text-slate-400">
                                                            {t('labworklist.n_tests', '{{n}} test(s)', { n: toNum(row.testCount) })}
                                                        </span>
                                                    )}
                                                </div>
                                                {(row.sectionNames || []).length > 0 && (
                                                    <div className="text-[10px] text-slate-400 mt-1">{row.sectionNames.join(' · ')}</div>
                                                )}
                                            </td>

                                            <td className="p-3">
                                                <ResultProgress row={row} />
                                                {critical > 0 && (
                                                    <span
                                                        data-testid={`critical-${row.orderId}`}
                                                        className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-md bg-red-600 text-white text-[10px] font-black uppercase tracking-wider"
                                                    >
                                                        <Siren className="w-3 h-3" />
                                                        {t('labworklist.critical_n', '{{n}} critical', { n: critical })}
                                                    </span>
                                                )}
                                            </td>

                                            <td className="p-3 text-xs text-gray-600">
                                                <div className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                                                    <Clock className="w-3 h-3" />
                                                    {fmtClock(row.receivedAt || row.collectedAt || row.orderedAt) || '—'}
                                                </div>
                                                {overdueMins !== null && (
                                                    <div
                                                        className={`text-[11px] font-semibold mt-1 ${overdueMins < 0 ? 'text-red-600' : overdueMins < 60 ? 'text-amber-600' : 'text-slate-400'}`}
                                                    >
                                                        {overdueMins < 0
                                                            ? t('labworklist.overdue_by', 'Overdue by {{d}}', { d: fmtDelay(overdueMins) })
                                                            : t('labworklist.due_in', 'Due in {{d}}', { d: fmtDelay(overdueMins) })}
                                                    </div>
                                                )}
                                            </td>

                                            <td className="p-3">
                                                <div className="flex justify-center">{primaryAction(row)}</div>
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                    <tr>
                                        <td colSpan={7} className="p-8 text-center text-gray-500 text-sm">
                                            {searching
                                                ? t('labworklist.no_match', 'Nothing matches that. Accession numbers look like L26-000123.')
                                                : t('labworklist.empty_bucket', 'Nothing here — every sample at this stage has moved on.')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Cards. The phlebotomist collecting on a ward round is holding a phone. */}
                    <div className="block lg:hidden space-y-3">
                        {rows.length > 0 ? rows.map((row) => {
                            const priority = String(row.priority || 'routine').toLowerCase();
                            const style = PRIORITY_STYLES[priority];
                            const overdueMins = minutesUntil(row.dueAt);
                            return (
                                <div
                                    key={row.orderId}
                                    data-testid={`lab-card-${row.orderId}`}
                                    className="flex rounded-xl shadow-md overflow-hidden bg-gray-50"
                                >
                                    <span className={`w-1.5 ${style?.rail || 'bg-slate-200'}`} />
                                    <div className="flex-1 p-3">
                                        <div className="flex justify-between items-start gap-2">
                                            <AccessionBadge accessionNo={row.accessionNo} testId={`accession-card-${row.orderId}`} />
                                            <PriorityBadge priority={priority} />
                                        </div>
                                        <div className="mt-2 font-semibold text-gray-800 text-sm">
                                            {row.patientName || t('common.patient_label', 'Patient')}
                                        </div>
                                        <div className="text-[11px] text-slate-400">
                                            {[row.patientAge, row.patientGender, hospitalPatientCode(row)].filter(Boolean).join(' · ')}
                                        </div>
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {(row.tests || []).slice(0, 4).map((test) => (
                                                <span key={test.testId} className="px-2 py-0.5 rounded bg-white border border-gray-200 text-[11px] text-gray-600">
                                                    {test.name}
                                                </span>
                                            ))}
                                        </div>
                                        <div className="mt-2">
                                            <ResultProgress row={row} />
                                        </div>
                                        {overdueMins !== null && overdueMins < 0 && (
                                            <div className="text-[11px] font-semibold text-red-600 mt-1">
                                                {t('labworklist.overdue_by', 'Overdue by {{d}}', { d: fmtDelay(overdueMins) })}
                                            </div>
                                        )}
                                        <div className="mt-3">{primaryAction(row)}</div>
                                    </div>
                                </div>
                            );
                        }) : (
                            <div className="p-6 text-center text-gray-500 text-sm">
                                {t('labworklist.empty_bucket', 'Nothing here — every sample at this stage has moved on.')}
                            </div>
                        )}
                    </div>
                </>
            )}

            {!loading && total > 0 && (
                <Pagination
                    totalItems={total}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                />
            )}

            <CollectModal
                open={!!collectRow}
                row={collectRow}
                busy={!!busyOrderId}
                onClose={() => setCollectRow(null)}
                onConfirm={handleCollect}
            />
        </div>
    );
}
